import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Badge, Table, Form, Spinner, Pagination, Alert } from "react-bootstrap";
import api from "../../utils/ApiConfig";

import { getRaisonLabel } from "./raisonsAbandon";

function getDateRange(jours) {
  const fin = new Date();
  const debut = new Date();
  debut.setDate(debut.getDate() - Number(jours));
  const toIsoDate = (d) => d.toISOString().slice(0, 10);
  return { date_debut: toIsoDate(debut), date_fin: toIsoDate(fin) };
}

function formatCommanditaire(row) {
  const raisonSociale = row.fournisseur?.user_profil?.RaisonSocial;
  const prenom = row.fournisseur?.prenom || "";
  const nom = row.fournisseur?.nom || "";
  const nomComplet = `${prenom} ${nom}`.trim();
  if (raisonSociale) return raisonSociale;
  if (nomComplet) return nomComplet;
  return row.fournisseur?.email || "—";
}

export default function FormAbandonmentsScreen() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    periode: "30",
    formulaire_complet: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { date_debut, date_fin } = getDateRange(filters.periode);

      const params = new URLSearchParams({ page, per_page: 20, date_debut, date_fin });
      if (filters.formulaire_complet !== "") {
        params.set("formulaire_complet", filters.formulaire_complet);
      }

      const [listRes, statsRes] = await Promise.all([
        api.get(`/bo/form-abandonments?${params}`),
        api.get(`/bo/form-abandonments/stats?date_debut=${date_debut}&date_fin=${date_fin}`),
      ]);

      setData(listRes.data.data);
      setStats(statsRes.data.stats);
    } catch (err) {
      setError("Erreur lors du chargement des tentatives de prix.");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const renderPagination = () => {
    if (!data || data.last_page <= 1) return null;
    const items = [];
    for (let i = 1; i <= data.last_page; i++) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>{i}</Pagination.Item>
      );
    }
    return <Pagination size="sm" className="mt-3 justify-content-center">{items}</Pagination>;
  };

  return (
    <Container fluid className="py-4">
      <h1 className="h3 mb-1">Tentatives de prix abandonnées</h1>
      <p className="text-muted small mb-4">
        Demandes de prix (pickup / livraison) entamées sur le formulaire de commande qui n'ont pas abouti à une commande.
      </p>

      {/* KPIs */}
      {stats && (
        <Row className="mb-4 g-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.total_abandons}</div>
                <div className="text-muted small mt-1">Tentatives sur la période</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.taux_abandon_avec_prix}</div>
                <div className="text-muted small mt-1">Ont vu un prix avant d'abandonner</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {stats.temps_moyen_remplissage_secondes ? `${stats.temps_moyen_remplissage_secondes}s` : "—"}
                </div>
                <div className="text-muted small mt-1">Temps moyen de remplissage</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="text-muted small mb-2">Raisons d'abandon</div>
                {stats.raisons_abandons?.length > 0 ? (
                  stats.raisons_abandons.map((r) => (
                    <div key={r.raison_abandon || "inconnue"} className="d-flex justify-content-between small">
                      <span>{getRaisonLabel(r.raison_abandon)}</span>
                      <span className="fw-semibold">{r.total}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtres */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-semibold">Période</Form.Label>
              <Form.Select size="sm" value={filters.periode} onChange={(e) => handleFilter("periode", e.target.value)}>
                <option value="7">7 derniers jours</option>
                <option value="30">30 derniers jours</option>
                <option value="90">90 derniers jours</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Formulaire</Form.Label>
              <Form.Select
                size="sm"
                value={filters.formulaire_complet}
                onChange={(e) => handleFilter("formulaire_complet", e.target.value)}
              >
                <option value="">Tous</option>
                <option value="1">A vu un prix (complet)</option>
                <option value="0">Abandonné avant le prix</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tableau */}
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0" style={{ fontSize: 14 }}>
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Commanditaire</th>
                    <th>Pickup</th>
                    <th>Livraison</th>
                    <th>Poids</th>
                    <th>Prix TTC</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-muted py-4">Aucune tentative sur cette période.</td></tr>
                  ) : (
                    data?.data?.map((r) => (
                      <tr key={r.id}>
                        <td className="text-nowrap">
                          {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="small">{formatCommanditaire(r)}</td>
                        <td className="text-muted small" style={{ maxWidth: 220, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {r.adresse_ramassage || <span className="text-muted">—</span>}
                        </td>
                        <td className="text-muted small" style={{ maxWidth: 220, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {r.adresse_livraison || <span className="text-muted">—</span>}
                        </td>
                        <td className="small">{r.poids || <span className="text-muted">—</span>}</td>
                        <td className="small">{r.prix_calcule_ttc ? `${r.prix_calcule_ttc} €` : <span className="text-muted">—</span>}</td>
                        <td>
                          <Badge bg={r.formulaire_complet ? "success" : "secondary"} className="fw-normal">
                            {r.formulaire_complet ? "Prix vu" : "Sans prix"}
                          </Badge>
                          <div className="text-muted" style={{ fontSize: 11 }}>{getRaisonLabel(r.raison_abandon)}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {renderPagination()}
          {data && (
            <p className="text-muted small text-center mt-2">
              {data.total} tentative{data.total > 1 ? "s" : ""} — page {data.current_page}/{data.last_page}
            </p>
          )}
        </>
      )}
    </Container>
  );
}
